<script setup lang="ts">
import { computed, inject, onMounted, ref, Ref, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import cloneDeep from 'lodash/cloneDeep';
import { LANG_ENGLISH, LAND_ONLY, STATUS_CLOSED } from '@/constants';
import { useHotelManagementStore } from '@/store/HotelManagementStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useViewComposable } from '@/views/composable';
import { useHotelStore } from '@/store/HotelStore';
import { useToast } from 'primevue/usetoast';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import HotelSearch from './hotels/HotelSearch.vue';
import HotelDateTypes from './hotels/HotelDateTypes.vue';
import HotelExcludeRoom from './hotels/HotelExcludeRoom.vue';
import HotelTargets from './hotels/HotelTargets.vue';

const dialogRef = inject('dialogRef') as Ref;
const isSave = dialogRef?.value?.options?.emits?.isSave;
const isEdit = dialogRef?.value?.options?.data?.isEdit;
const dataHotel = dialogRef?.value?.options?.data?.data;
const rowDataHotel = dialogRef?.value?.options?.data?.rowDataHotel;
const destinations = dialogRef?.value?.options?.data?.destinations;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const isLoading = ref(false);
const groupTypeList = ref([]);
const isDisable = ref(true);
const filterDestination = ref();
const groupTypeManagement = useGroupTypeStore();
const { apiOrder, convertTimeZero } = useViewComposable();
const hotelManagementStore = useHotelManagementStore();
const currentLanguage = getCurrentLocale();
const hotelStore = useHotelStore();
const { hotelRoomCategory: selectHotelRooms } = storeToRefs(hotelStore);
const disableSoftVoyge = ref(true);

const convertRoomCategories = (categories: any) => {
  return categories?.map((ranges: any) => {
    return convertDate(ranges);
  });
};

const convertDate = (ranges: any) => {
  ranges?.ranges.map((range: any) => {
    for (const k in range) {
      if (k === 'dateDep' && range[k] && typeof range[k] !== 'object') {
        range[k] = formatDateUTC(range[k]);
      }
      if (k === 'dateRet' && range[k] && typeof range[k] !== 'object') {
        range[k] = formatDateUTC(range[k]);
      }
    }
    return range;
  });
  return ranges;
};

const formatDateUTC = (dateString: any) => {
  return new Date(DateTime.fromISO(dateString));
};

const filterDestinationByCode = (destinations: any, code: string) => {
  return destinations?.find((destination: any) => destination.code === code);
};

const formatListDate = (listDate: any) => {
  if (listDate?.length > 0) {
    return listDate?.map((date: any) => {
      return {
        start: new Date(DateTime.fromISO(date.start)),
        end: new Date(DateTime.fromISO(date.end))
      };
    });
  } else {
    return [];
  }
};

const formatHotelData = (data: any) => {
  data.excludeRoomCategoryHotel.categories = convertRoomCategories(data.excludeRoomCategoryHotel.categories);
  data.excludeRoomCategoryPackage.categories = convertRoomCategories(data.excludeRoomCategoryPackage.categories);
  data.excludeRoomCategoryHotel.manualCategories = convertRoomCategories(data.excludeRoomCategoryHotel.manualCategories);
  data.excludeRoomCategoryPackage.manualCategories = convertRoomCategories(data.excludeRoomCategoryPackage.manualCategories);
  const getDestSVId = filterDestinationByCode(destinations, data?.destSVId);
  return {
    ...data,
    travelDate: formatListDate(data.travelDate),
    bookingDate: formatListDate(data.bookingDate),
    blackoutDate: formatListDate(data.blackoutDate),
    destSVId: getDestSVId ?? data?.destSVId,
    destSVIdOld: data.destSVId,
    excludeRoomCategoryHotel: data.excludeRoomCategoryHotel,
    excludeRoomCategoryPackage: data.excludeRoomCategoryPackage
  };
};

const hotel: any = ref(
  dataHotel
    ? cloneDeep(formatHotelData(dataHotel))
    : {
        hCode: '',
        code: '',
        name: '',
        nameFr: '',
        chainName: '',
        chainNameFr: '',
        insta: STATUS_CLOSED,
        instaHotel: STATUS_CLOSED,
        status: STATUS_CLOSED,
        statusHotel: STATUS_CLOSED,
        destName: '',
        destNameFr: '',
        maxRooms: 0,
        quoteExpiryRestriction: 0,
        travelDate: [],
        bookingDate: [],
        isPackage: true,
        isHotel: false,
        blackoutDate: [],
        groupTypes: [],
        roomCategoryCallOrder: '',
        excludeHotelRoom: [],
        destSVId: '',
        destSVIdOld: '',
        excludeRoomCategoryHotel: {
          categories: [],
          manualCategories: []
        },
        excludeRoomCategoryPackage: {
          categories: [],
          manualCategories: []
        }
      }
);

const clearRoomCategoriesRef = ref();

const emptyHotelFactory = () => {
  return {
    hCode: '',
    code: '',
    name: '',
    chainName: '',
    chainNameFr: '',
    bookingDate: [],
    isPackage: true,
    isHotel: false,
    blackoutDate: [],
    groupTypes: [],
    roomCategoryCallOrder: '',
    excludeHotelRoom: [],
    destSVId: '',
    nameFr: '',
    insta: STATUS_CLOSED,
    instaHotel: STATUS_CLOSED,
    status: STATUS_CLOSED,
    statusHotel: STATUS_CLOSED,
    destName: '',
    destNameFr: '',
    maxRooms: 0,
    quoteExpiryRestriction: 0,
    excludeRoomCategoryHotel: {
      categories: [],
      manualCategories: []
    },
    excludeRoomCategoryPackage: {
      categories: [],
      manualCategories: []
    },
    travelDate: [],
    destSVIdOld: ''
  };
};

const clearFormHotel = () => {
  excludeRoomCategory.value = {
    categories: [],
    manualCategories: []
  };
  clearRoomCategoriesRef.value.clearRoomCategory();
  Object.assign(hotel.value, emptyHotelFactory());
  isDisable.value = true;
  disableSoftVoyge.value = true;
};
const rulesForExclude = {
  categories: {
    $each: helpers.forEach({
      ranges: {
        $each: helpers.forEach({
          dateDep: { required: helpers.withMessage(t('validate.required'), required) },
          dateRet: { required: helpers.withMessage(t('validate.required'), required) }
        })
      }
    })
  },
  manualCategories: {
    $each: helpers.forEach({
      ranges: {
        $each: helpers.forEach({
          dateDep: { required: helpers.withMessage(t('validate.required'), required) },
          dateRet: { required: helpers.withMessage(t('validate.required'), required) }
        })
      }
    })
  }
};

const rules = ref({
  groupTypes: { required },
  name: { required },
  nameFr: { required },
  destName: { required },
  destNameFr: { required },
  maxRooms: { required },
  roomCategoryCallOrder: { required },
  excludeRoomCategoryHotel: rulesForExclude,
  excludeRoomCategoryPackage: rulesForExclude,
  blackoutDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  },
  travelDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  },
  bookingDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  }
});

const excludeRoomCategory = ref({
  categories: [
    {
      category: '',
      name: '',
      ranges: [
        {
          dateDep: null,
          dateRet: null
        }
      ]
    }
  ],
  manualCategories: [
    {
      categories: [],
      ranges: [
        {
          dateDep: null,
          dateRet: null
        }
      ]
    }
  ]
});

const emptyExcludeRoomCategory = () => {
  return {
    categories: [],
    manualCategories: []
  };
};

const getGroupTypeData = async () => {
  isLoading.value = true;
  groupTypeManagement
    .getGroupTypeList()
    .then(async (response) => {
      groupTypeList.value = response.data;
    })
    .finally(() => (isLoading.value = false));
};
getGroupTypeData();

const v$: any = useVuelidate(rules.value, hotel.value);

const checkPayloadEdit = (data: any, payload: any) => {
  if (!data.isPackage) {
    payload = {
      ...payload,
      excludeRoomCategoryPackage: emptyExcludeRoomCategory()
    };
    return payload;
  }
  if (!data.isHotel) {
    payload = {
      ...payload,
      excludeRoomCategoryHotel: emptyExcludeRoomCategory()
    };
    return payload;
  }
  if (data.isPackage) {
    payload = {
      ...payload,
      excludeRoomCategoryPackage: formatExcludeRoomCategory(data?.excludeRoomCategoryPackage)
    };
    return payload;
  }
  if (data.isHotel) {
    payload = {
      ...payload,
      excludeRoomCategoryHotel: formatExcludeRoomCategory(data?.excludeRoomCategoryHotel)
    };
    return payload;
  }
};

const submitPayload = ref({});

watch(submitPayload.value, (newValue) => {
  if (newValue) {
    v$.value = useVuelidate(rules.value, newValue);
  }
});

const handleFormatListExcludeRoom = (listData: any) => {
  if (listData?.length) {
    return listData.map((category: any) => {
      return {
        ...category,
        ranges: convertTimeZero(category.ranges, 'excludeCategory')
      };
    });
  } else {
    return [];
  }
};

const formatExcludeRoomCategory = (data: any) => {
  return {
    categories: handleFormatListExcludeRoom(data.categories),
    manualCategories: handleFormatListExcludeRoom(data.manualCategories)
  };
};

const handleSubmitPayload = (isEditModal: boolean, data: any) => {
  if (isEditModal) {
    if (!data?.isPackageTab) {
      submitPayload.value = {
        ...submitPayload.value,
        maxRoomsHotel: data?.maxRooms,
        excludeHotelRoomHotel: data?.excludeHotelRoom,
        groupTypesHotel: data?.groupTypes,
        roomCategoryCallOrderHotel: data?.roomCategoryCallOrder,
        status: data?.status,
        insta: data?.insta,
        statusHotel: data?.statusHotel,
        instaHotel: data?.instaHotel,
        blackoutDateHotel: convertTimeZero(data?.blackoutDate),
        travelDateHotel: convertTimeZero(data?.travelDate),
        bookingDateHotel: convertTimeZero(data?.bookingDate),
        hotelTypeHotel: data?.hotelType,
        excludeRoomCategoryHotel: formatExcludeRoomCategory(excludeRoomCategory.value),
        tabType: 'HOTEL'
      };
      submitPayload.value = checkPayloadEdit(data, submitPayload.value);
    } else {
      submitPayload.value = {
        ...submitPayload.value,
        maxRooms: data?.maxRooms,
        excludeHotelRoom: data?.excludeHotelRoom,
        groupTypes: data?.groupTypes,
        roomCategoryCallOrder: data?.roomCategoryCallOrder,
        status: data?.status,
        insta: data?.insta,
        statusHotel: data?.statusHotel,
        instaHotel: data?.instaHotel,
        blackoutDate: convertTimeZero(data?.blackoutDate),
        travelDate: convertTimeZero(data?.travelDate),
        bookingDate: convertTimeZero(data?.bookingDate),
        hotelType: data?.hotelType,
        excludeRoomCategoryPackage: formatExcludeRoomCategory(excludeRoomCategory.value),
        tabType: 'PACKAGE'
      };
      submitPayload.value = checkPayloadEdit(data, submitPayload.value);
    }
  } else {
    if (data?.isHotel) {
      submitPayload.value = {
        ...submitPayload.value,
        maxRoomsHotel: data?.maxRooms,
        excludeHotelRoomHotel: data?.excludeHotelRoom,
        groupTypesHotel: data?.groupTypes,
        roomCategoryCallOrderHotel: data?.roomCategoryCallOrder,
        status: data?.status,
        insta: data?.insta,
        statusHotel: data?.statusHotel,
        instaHotel: data?.instaHotel,
        blackoutDateHotel: convertTimeZero(data?.blackoutDate),
        travelDateHotel: convertTimeZero(data?.travelDate),
        bookingDateHotel: convertTimeZero(data?.bookingDate),
        hotelTypeHotel: 'MANUAL',
        excludeRoomCategoryHotel: formatExcludeRoomCategory(excludeRoomCategory.value)
      };
    }
    if (data?.isPackage) {
      submitPayload.value = {
        ...submitPayload.value,
        maxRooms: data?.maxRooms,
        excludeHotelRoom: data?.excludeHotelRoom,
        groupTypes: data?.groupTypes,
        roomCategoryCallOrder: data?.roomCategoryCallOrder,
        status: data?.status,
        insta: data?.insta,
        statusHotel: data?.statusHotel,
        instaHotel: data?.instaHotel,
        blackoutDate: convertTimeZero(data?.blackoutDate),
        travelDate: convertTimeZero(data?.travelDate),
        bookingDate: convertTimeZero(data?.bookingDate),
        hotelType: 'MANUAL',
        excludeRoomCategoryPackage: formatExcludeRoomCategory(excludeRoomCategory.value)
      };
    }
  }
};

const checkDataObject = (data: any) => {
  return typeof data?.destSVId === 'object' ? data?.destSVId?.code : data?.destSVId;
};

const getDestSVId = (isEditModal: boolean, data: any) => {
  return !isEditModal ? data?.destSVId : checkDataObject(data);
};

const checkDestSVId = (isEditModal: boolean, data: any) => {
  const destSVId = getDestSVId(isEditModal, data);
  return typeof destSVId === 'object' ? destSVId?.code : destSVId;
};

const handleCallSubmit = async (data: any, isEditModal: boolean) => {
  isLoading.value = true;
  submitPayload.value = {
    hCode: data?.hCode.toUpperCase(),
    code: data?.code,
    name: data?.name,
    nameFr: data?.nameFr,
    chainName: data?.chainName,
    chainNameFr: data?.chainNameFr,
    destName: data?.destName,
    destNameFr: data?.destNameFr,
    isPackage: data?.isPackage,
    isHotel: data?.isHotel,
    quoteExpiryRestriction: data?.quoteExpiryRestriction ?? 0,
    destSVId: checkDestSVId(isEditModal, data),
    destSVIdOld: getDestSVId(isEditModal, data) === data?.destSVIdOld ? '' : data?.destSVIdOld,
    excludeRoomCategoryHotel: data?.excludeRoomCategoryHotel,
    excludeRoomCategoryPackage: data?.excludeRoomCategoryPackage
  };
  submitPayload.value = checkPayloadEdit(data, submitPayload.value);
  handleSubmitPayload(isEditModal, data);
  v$.value.$validate();
  if (!v$.value.$error) {
    await hotelManagementStore
      .addNewHotel({ ...rowDataHotel, ...submitPayload.value })
      .then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        isSave();
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
      })
      .finally(() => {
        isLoading.value = false;
        selectHotelRooms.value = new Map();
      });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.fill_required') });
  }
  isLoading.value = false;
};

const addNewDate = (type: string) => {
  hotel.value[type].push({
    start: null,
    end: null
  });
};

const removeDate = (type: string, index: number) => {
  hotel.value[type] = hotel.value[type].filter((_item: any, indexDate: number) => indexDate !== index);
};

const addNewCategory = (type: string) => {
  if (type === 'categories') {
    if (excludeRoomCategory.value?.categories === null) {
      excludeRoomCategory.value.categories = [];
    }
    excludeRoomCategory.value?.categories.push({
      category: '',
      name: '',
      ranges: [
        {
          dateDep: null,
          dateRet: null
        }
      ]
    });
  } else {
    if (excludeRoomCategory.value?.manualCategories === null) {
      excludeRoomCategory.value.manualCategories = [];
    }
    excludeRoomCategory.value?.manualCategories?.push({
      categories: [],
      ranges: [
        {
          dateDep: null,
          dateRet: null
        }
      ]
    });
  }
};

const removeCategory = (event: any) => {
  if (event.type === 'categories') {
    excludeRoomCategory.value.categories = excludeRoomCategory.value.categories.filter((_item: any, indexCategory: number) => indexCategory !== event.indexItem);
  } else {
    excludeRoomCategory.value.manualCategories = excludeRoomCategory.value.manualCategories.filter((_item: any, indexCategory: number) => indexCategory !== event.indexItem);
  }
};

const addNewBlackoutDate = (event: any) => {
  if (event?.type === 'categories') {
    excludeRoomCategory.value?.categories[event?.indexItem]?.ranges.push({
      dateDep: null,
      dateRet: null
    });
  } else {
    excludeRoomCategory.value?.manualCategories[event?.indexItem]?.ranges.push({
      dateDep: null,
      dateRet: null
    });
  }
};

const updateRanges = (categoryType: string, categoryIndex: number, dateIndex: any) => {
  const category = excludeRoomCategory.value?.[categoryType][categoryIndex];
  if (category?.ranges) {
    category.ranges = category.ranges.filter((_item: any, indexCategory: number) => indexCategory !== dateIndex);
  }
};

const removeBlackoutDate = (event: any) => {
  if (event?.type === 'categories') {
    updateRanges('categories', event.categoryIndex, event.dateIndex);
  } else {
    updateRanges('manualCategories', event.categoryIndex, event.dateIndex);
  }
};

const changeRoomCategory = (event: any) => {
  excludeRoomCategory.value.categories[event.index].category = event.room;
  excludeRoomCategory.value.categories[event.index].name = event.roomName;
};

const populateHotelInfo = async (hotelInfo: any) => {
  Object.assign(hotel.value, hotelInfo);
  hotel.value.excludeRoomCategoryHotel = emptyExcludeRoomCategory();
  hotel.value.excludeRoomCategoryPackage = emptyExcludeRoomCategory();
  excludeRoomCategory.value = emptyExcludeRoomCategory();
  hotel.value.blackoutDate = [];
  hotel.value.travelDate = [];
  hotel.value.bookingDate = [];
  hotel.value.maxRooms = 0;
  hotel.value.roomCategoryCallOrder = LAND_ONLY;
  hotel.value.isPackage = true;
  hotel.value.groupTypes = hotelInfo.notExist ? [] : groupTypeList.value.map((groupType: any) => groupType?.sk);
  hotel.value.excludeRoomCategoryHotel = {
    categories: [],
    manualCategories: []
  };
  hotel.value.excludeRoomCategoryPackage = {
    categories: [],
    manualCategories: []
  };
  isDisable.value = false;
  disableSoftVoyge.value = true;
};

const validateSwitch = (event: any) => {
  if (event.type === 'PACKAGE_ONLY') {
    if (event.switchType === 'insta') {
      hotel.value.insta = event.value.insta;
    } else {
      hotel.value.status = event.value.status;
    }
  } else if (event.type === 'HOTEL_ONLY') {
    if (event.switchType === 'insta') {
      hotel.value.instaHotel = event.value.instaHotel;
    } else {
      hotel.value.statusHotel = event.value.statusHotel;
    }
  }
};

const validateCheckbox = (event: any) => {
  if (event?.type === 'PACKAGE') {
    hotel.value.isPackage = event.hotelTargets?.isPackage;
  }
  if (event?.type === 'HOTEL') {
    hotel.value.isHotel = event.hotelTargets?.isHotel;
  }
};

const searchDestination = (event: any) => {
  if (!event?.query?.trim().length) {
    filterDestination.value = [...destinations];
  } else {
    filterDestination.value = destinations.filter((destination: any) => {
      return destination.code?.toLowerCase().startsWith(event.query.toLowerCase()) || destination.name?.toLowerCase().startsWith(event.query.toLowerCase()) || destination.nameFr?.toLowerCase().startsWith(event.query.toLowerCase());
    });
  }
};

const labelSubmit = computed(() => {
  if (isEdit) {
    if (!hotel.value.isPackageTab) {
      return t('payment.labels.update_hotel');
    } else {
      return t('payment.labels.update_package_hotel');
    }
  } else {
    return t('payment.labels.submit');
  }
});

watch(
  excludeRoomCategory,
  (newVal) => {
    if (hotel.value.isPackageTab) {
      hotel.value.excludeRoomCategoryPackage = newVal;
    } else {
      hotel.value.excludeRoomCategoryHotel = newVal;
    }
  },
  { deep: true }
);

const checkRulesForEdit = () => {
  if (isEdit) {
    if (hotel.value.isPackageTab) {
      delete rules.value.excludeRoomCategoryHotel;
    } else {
      delete rules.value.excludeRoomCategoryPackage;
    }
  }
};
onMounted(() => {
  if (hotel.value.isPackageTab) {
    excludeRoomCategory.value = hotel.value.excludeRoomCategoryPackage;
  } else {
    excludeRoomCategory.value = hotel.value.excludeRoomCategoryHotel;
  }
  checkRulesForEdit();
});
</script>
<template>
  <div class="add-hotel-modal">
    <div>
      <Fluid v-if="!isEdit">
        <div class="field grid">
          <HotelSearch
            @foundHotelInfo="populateHotelInfo($event)"
            @clearHotel="clearFormHotel()"
            @setIsDisabled="
              isDisable = false;
              disableSoftVoyge = false;
            "
          />
          <div class="col-4">
            <label for="code">{{ $t('management.hotel.softVoyage_code') }} </label>
            <InputText id="code" data-testid="input_sv_code" :disabled="disableSoftVoyge" class="w-full" v-model="hotel.code" />
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col-8">
            <label for="hotel_name">{{ `${$t('management.hotel.hotel_name')} (${$t('common.english')})` }} <span class="text-primary">*</span></label>
            <InputText id="hotel_name" :disabled="isDisable && !isEdit" :class="v$.name.$error ? 'p-invalid w-full' : 'w-full'" v-model="hotel.name" />
            <p class="text-red-500 pt-1" v-if="v$.name.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-4">
            <label for="chain_name">{{ `${$t('management.hotel.chain_name')} (${$t('common.english')})` }}</label>
            <InputText id="chain_name" :disabled="isDisable && !isEdit" class="w-full" v-model="hotel.chainName" />
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col-8">
            <label for="name_fr">{{ `${$t('management.hotel.hotel_name')} (${$t('common.french')})` }} <span class="text-primary">*</span></label>
            <InputText id="name_fr" :disabled="isDisable && !isEdit" :class="v$.nameFr.$error ? 'p-invalid w-full' : 'w-full'" v-model="hotel.nameFr" />
            <p class="text-red-500 pt-1" v-if="v$.nameFr.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-4">
            <label for="chain_name_fr">{{ `${$t('management.hotel.chain_name')} (${$t('common.french')})` }} </label>
            <InputText id="chain_name_fr" :disabled="isDisable && !isEdit" class="w-full" v-model="hotel.chainNameFr" />
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col-4">
            <label for="dest_name">{{ `${$t('management.hotel.destination_region')} (${$t('common.english')})` }} <span class="text-primary">*</span></label>
            <InputText id="dest_name" :disabled="isDisable && !isEdit" :class="v$.destName.$error ? 'p-invalid w-full' : 'w-full'" v-model="hotel.destName" />
            <p class="text-red-500 pt-1" v-if="v$.destName.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-4">
            <label for="dest_name_fr">{{ `${$t('management.hotel.destination_region')} (${$t('common.french')})` }} <span class="text-primary">*</span></label>
            <InputText id="dest_name_fr" :disabled="isDisable && !isEdit" :class="v$.destNameFr.$error ? 'p-invalid w-full' : 'w-full'" class="w-full" v-model="hotel.destNameFr" />
            <p class="text-red-500 pt-1" v-if="v$.destNameFr.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-4 group-type-selector">
            <div class="w-full">
              <label for="destSVId">{{ $t('management.package.sv_destination_code') }}</label>
              <AutoComplete id="destSVId" v-model="hotel.destSVId" :suggestions="filterDestination" completeOnFocus @complete="searchDestination($event)" optionLabel="code" optionValue="code" :delay="500" class="w-full">
                <template #option="slotProps">
                  <div class="flex align-options-center">
                    <div>
                      <div v-if="currentLanguage === 'fr'">{{ slotProps.option.code }} - {{ slotProps.option.nameFr }}</div>
                      <div v-else>{{ slotProps.option.code }} - {{ slotProps.option.name }}</div>
                    </div>
                  </div>
                </template>
              </AutoComplete>
            </div>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col-8">
            <div class="flex gap-2">
              <div class="w-full">
                <label for="max_room">{{ $t('management.hotel.maximum_rooms') }} <span class="text-primary">*</span></label>
                <InputNumber :min="0" :max="999" :class="v$.maxRooms.$error ? 'p-invalid w-full' : 'w-full'" id="max_room" :disabled="isDisable && !isEdit" class="w-full" v-model="hotel.maxRooms" />
                <p class="text-red-500 pt-1" v-if="v$.maxRooms.$error">{{ $t('validate.required') }}</p>
              </div>
              <div class="w-full">
                <label for="quote_expiry_restriction">{{ $t('management.hotel.quote_expiry_restriction') }} </label>
                <InputNumber id="quote_expiry_restriction" :min="0" :max="999" class="w-full" :disabled="isDisable && !isEdit" v-model="hotel.quoteExpiryRestriction" />
              </div>
            </div>
          </div>
          <div class="col-4">
            <label for="group_type">{{ $t('management.hotel.group_type_select') }} <span class="text-primary">*</span></label>
            <MultiSelect
              id="group_type"
              :disabled="isDisable && !isEdit"
              v-model="hotel.groupTypes"
              display="chip"
              :options="groupTypeList"
              :optionLabel="currentLanguage === LANG_ENGLISH ? 'name' : 'nameFr'"
              :maxSelectedLabels="3"
              optionValue="sk"
              :placeholder="$t('advance_quote.form.select_group_type')"
              :class="v$.groupTypes.$error ? 'p-invalid w-full' : 'w-full'"
            >
              <template #option="slotProps">
                <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr']">
                  {{ currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr'] }}
                </div>
              </template>
            </MultiSelect>
            <p class="text-red-500 pt-1" v-if="v$.groupTypes.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col-8"></div>
          <div class="col-4">
            <label for="room_category_call_order">{{ $t('management.hotel.api_call_order') }} <span class="text-primary">*</span></label>
            <Select
              id="room_category_call_order"
              :disabled="isDisable && !isEdit"
              :class="v$.roomCategoryCallOrder.$error ? 'table-select p-invalid w-full' : 'w-full'"
              :options="apiOrder"
              optionLabel="label"
              optionValue="value"
              v-model="hotel.roomCategoryCallOrder"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <div class="overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type) => type.value === slotProps.value)?.label }}</div>
                </div>
              </template>
            </Select>
            <p class="text-red-500 pt-1" v-if="v$.roomCategoryCallOrder.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid border-dot-line">
          <div class="col-4">
            <HotelDateTypes :title="$t('hotel_contract.booking_date_range')" :dateRanges="hotel.bookingDate" @addNewDate="addNewDate('bookingDate')" @removeDate="removeDate('bookingDate', $event)" />
          </div>
          <div class="col-4">
            <HotelDateTypes :title="$t('hotel_contract.travel_date_range')" :dateRanges="hotel.travelDate" @addNewDate="addNewDate('travelDate')" @removeDate="removeDate('travelDate', $event)" />
          </div>
          <div class="col-4">
            <HotelDateTypes :title="$t('hotel_contract.black_out_date')" :dateRanges="hotel.blackoutDate" @addNewDate="addNewDate('blackoutDate')" @removeDate="removeDate('blackoutDate', $event)" />
          </div>
        </div>
      </Fluid>
      <Fluid class="border-dot-line pl-2">
        <HotelExcludeRoom
          :isEdit="isEdit"
          :title="$t('management.hotel.exclude_room_categories')"
          :excludeRoomCategory="excludeRoomCategory"
          :hotel="hotel"
          :destinations="destinations"
          ref="clearRoomCategoriesRef"
          @addNewCategory="addNewCategory($event)"
          @removeCategory="removeCategory($event)"
          @addNewBlackoutDate="addNewBlackoutDate($event)"
          @removeBlackoutDate="removeBlackoutDate($event)"
          @changeRoomCategory="changeRoomCategory($event)"
        />
      </Fluid>
      <Fluid>
        <HotelTargets :isEdit="isEdit" :isPackageTab="hotel.isPackageTab" :hotel="hotel" @validateCheckbox="validateCheckbox($event)" @validateSwitch="validateSwitch($event)" />
      </Fluid>
    </div>
    <div class="text-center mt-5">
      <Button :disabled="isDisable && !isEdit" :loading="isLoading" :label="labelSubmit" @click="handleCallSubmit(hotel, isEdit)" data-testid="submit-btn"></Button>
    </div>
  </div>
</template>
